import React from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { centsToDollarString } from 'utils';

import ActionButton from 'components/action-button';
import Currency from 'components/currency';
import Dialog from 'components/dialog';
import Link from 'components/link';
import { useUIContext } from 'state/ui';
import { platform } from 'utils/environment';

import { useActiveTieredDeliveryFees } from '../hooks/use-active-tiered-delivery-fees';

import { BodyContainer, ItemContainer, SubtitleContainer } from './styled';
import { IDeliveryFeesModalProps } from './types';

const TermsLink = styled(Link)`
  color: ${Styles.color.primary};
`;

const DeliveryFeesModal: React.FC<IDeliveryFeesModalProps> = ({
  open,
  onClose,
  serviceMode,
  subTotalCents,
  deliveryFeeCents,
  smallCartFeeCents,
  serviceFeeCents,
  geographicalFeeCents,
  isLoading,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { smallCartFeeThreshold, tier } = useActiveTieredDeliveryFees({
    serviceMode,
    subTotalCents,
    platform: platform(),
  });
  const { formatMessage } = useIntl();
  if (!open) {
    return null;
  }
  return (
    <Dialog
      data-testid="delivery-fees-modal"
      heading={formatMessage({
        id: 'deliveryFees',
      })}
      bodyComponent={
        <BodyContainer>
          <ItemContainer data-testid="delivery-fee-item">
            <SubtitleContainer>
              {formatMessage({
                id: 'deliveryFee',
              })}
              {': '}
              <Currency amount={deliveryFeeCents} isLoading={isLoading} />
            </SubtitleContainer>
            {formatMessage(
              { id: 'deliveryFeeDescription' },
              {
                link: (
                  <TermsLink to={formatMessage({ id: 'routes.deliveryTerms' })}>
                    {formatMessage({ id: 'here' })}
                  </TermsLink>
                ),
              }
            )}
          </ItemContainer>
          {geographicalFeeCents > 0 && (
            <ItemContainer data-testid="geographical-fee-item">
              <SubtitleContainer>
                {formatMessage({
                  id: 'geographicalFee',
                })}
                {': '}
                <Currency amount={geographicalFeeCents} isLoading={isLoading} />
              </SubtitleContainer>
              {formatMessage(
                {
                  id: 'geographicalFeeDescription',
                },
                {
                  geographicalFeeAmount: centsToDollarString(geographicalFeeCents),
                }
              )}
            </ItemContainer>
          )}
          {serviceFeeCents > 0 && (
            <ItemContainer data-testid="service-fee-item">
              <SubtitleContainer>
                {formatMessage({
                  id: 'serviceFee',
                })}
                {': '}
                <Currency amount={serviceFeeCents} isLoading={isLoading} />
              </SubtitleContainer>
              {formatMessage(
                {
                  id: 'serviceFeeDescription',
                },
                {
                  percent: tier.serviceFeePercent,
                }
              )}
            </ItemContainer>
          )}
          {smallCartFeeCents > 0 && (
            <ItemContainer data-testid="small-cart-fee-item">
              <SubtitleContainer>
                {formatMessage({
                  id: 'smallCartFee',
                })}
                {': '}
                <Currency amount={smallCartFeeCents} isLoading={isLoading} />
              </SubtitleContainer>
              {smallCartFeeThreshold > 0 &&
                formatMessage(
                  {
                    id: 'smallCartFeeDescription',
                  },
                  {
                    amount: formatCurrencyForLocale(smallCartFeeThreshold),
                  }
                )}
            </ItemContainer>
          )}
        </BodyContainer>
      }
      actions={
        <Box margin="1rem" minWidth="auto" width="100%">
          <ActionButton
            onlyIcon
            fullWidth
            onClick={onClose}
            data-testid="delivery-fees-modal-ok-btn"
          >
            {formatMessage({
              id: 'okayButtonText',
            })}
          </ActionButton>
        </Box>
      }
      modalAppearanceEventMessage="Delivery Fees Modal"
      showCloseButton
      onDismiss={onClose}
    />
  );
};

export default DeliveryFeesModal;
